import { useEffect, useState } from "react";
import { ImageOff, Loader2, ZoomIn } from "lucide-react";
import { normalizeArtifactType } from "@/components/evidenceGathering/ArtifactTypeBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useArtifactFile } from "@/hooks/useArtifactFile";
import type { Artifact } from "@/services/projectsApi";

interface ArtifactThumbnailProps {
  artifact: Artifact;
  projectId: string;
  className?: string;
}

/** The picture itself, for checking a description against.
 *
 *  Charts and images only. A table has no stored picture — the card grid draws
 *  its preview from the CSV instead, which is worth doing at tile size and not
 *  worth it next to a line of text.
 *
 *  One fetch per rendered thumbnail. The file endpoint sends a long immutable
 *  cache and the URL is versioned by updatedAt, so a second look costs nothing,
 *  but the first pass over a freshly extracted model does fetch each one.
 *
 *  Renders a <button> once the picture has loaded, so it must not be placed
 *  inside another button — nested buttons are invalid, and the outer one would
 *  swallow the click. */
const ArtifactThumbnail = ({ artifact, projectId, className = "" }: ArtifactThumbnailProps) => {
  const kind = normalizeArtifactType(artifact.type);
  const isPicture = kind === "Chart" || kind === "Image";
  const [zoomed, setZoomed] = useState(false);
  const { blobUrl, isLoading, error, fetchFile, clearFile } = useArtifactFile();

  // updatedAt is read but deliberately not a dependency: it only versions the
  // URL against a re-extraction, and enrichment bumps it on every status change,
  // which would re-download the picture mid-description.
  useEffect(() => {
    if (isPicture && artifact.blobName) fetchFile(projectId, artifact.uid, false, artifact.updatedAt);
    return clearFile;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPicture, artifact.blobName, artifact.uid, projectId, fetchFile, clearFile]);

  if (!isPicture) return null;

  const frame = `shrink-0 flex items-center justify-center rounded border bg-muted/30 overflow-hidden ${className}`;
  const canZoom = !!blobUrl && !error && !isLoading;

  const inner = (
    <>
      {isLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
      {!isLoading && blobUrl && !error && (
        <img src={blobUrl} alt="" className="max-w-full max-h-full object-contain" />
      )}
      {!isLoading && (!blobUrl || error) && (
        <ImageOff className="w-3.5 h-3.5 text-muted-foreground/60" />
      )}
    </>
  );

  if (!canZoom) return <div className={frame}>{inner}</div>;

  return (
    <>
      <button
        type="button"
        onClick={() => setZoomed(true)}
        title="Open full size"
        aria-label={`Open ${artifact.name} full size`}
        className={`${frame} group relative cursor-zoom-in hover:border-brand-teal/50`}
      >
        {inner}
        <span className="absolute inset-0 hidden items-center justify-center bg-background/60 group-hover:flex">
          <ZoomIn className="w-5 h-5 text-foreground" />
        </span>
      </button>

      {/* A plain lightbox rather than ArtifactPreviewDialog: that one carries a
          description editor, which is already on screen immediately beside this
          thumbnail. Showing a second copy of it would invite editing the same
          text in two places. */}
      <Dialog open={zoomed} onOpenChange={setZoomed}>
        <DialogContent className="sm:max-w-[1320px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">{artifact.name}</DialogTitle>
          </DialogHeader>
          <div className="flex justify-center bg-muted/30 rounded-md p-4">
            <img src={blobUrl!} alt={artifact.name} className="max-w-full h-auto" />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ArtifactThumbnail;
